import * as moment from 'moment';

import {CliEmpAttributes, EmpContractAttributes} from '../../models/models.interfaces';
import {ApiSingleData, Data, Link} from '../api';
import {isPresent} from '../utils/lang';

export interface PayPeriodContract {

  id?: number;
  externalID?: string;
  firstName?: string;
  lastName?: string;
  payRollSerieName?: string;
  payRollPeriodsName?: string;
  nbLaunch?: number;
  status?: PayStatus;
  nbCriticalAlert?: number;
  nbTotalAlert?: number;
  nbExecution?: number;
  lastExecution?: ExecEntity;
  lastGoodExecution?: ExecEntity;
  contractID?: number;
  actions?: PayPeriodContractAction[];
  readOnly?: boolean;
  payPeriodId?: number;
  outsource?: boolean;
  payTypeName?: string;
  payTypeTransId?: string;
  payTypeCalculationType?: string;
  payTypeAllowLaunchAfterClose?: boolean;
  unalterable?: boolean;

  // Attributes used to display the status with the associated badge
  statusToDisplay?: string;
  badgeCssClasses?: string[];
}

export interface ExecEntity {
  id?: number;
  time?: Date;
  shortId?: number;
  requestPerYear?: string;
}

export enum PayPeriodContractAction {
  DONE = 'DONE',
  FIX = 'FIX',
  UNFREEZE = 'UNFREEZE',
  LAUNCH = 'LAUNCH',
  READY = 'READY',
  LAST_DONE = 'LAST_DONE',
  CREATE_NEXT_PPC = 'CREATE_NEXT_PPC'
}

export enum PayStatus {
  FUTURE = 'FUTURE',
  TODO = 'TODO',
  AGAIN = 'AGAIN',
  SCHEDULED = 'SCHEDULED',
  SENDED = 'SENDED',
  ONGOING = 'ONGOING',
  STORAGE_IN_PROGRESS = 'STORAGE_IN_PROGRESS',
  CALCULATION_DONE = 'CALCULATION_DONE',
  PAYSLIP_IN_PROGRESS = 'PAYSLIP_IN_PROGRESS',
  DONE = 'DONE',
  ALERT = 'ALERT',
  READY = 'READY',
  ERROR = 'ERROR',
  FROZEN = 'FROZEN',
  CLOSED = 'CLOSED',
  NOT_READY = 'NOT_READY'
}

/**
 * Options to search employees
 */
export interface SearchOptions {
  ftsSearch?: string;
  search?: string;
  page?: number;
  perPage?: number;
  orderBy?: string;
  seeInactiveEmployees?: boolean;
  seeInactiveContracts?: boolean;
}

export interface SelectEmployee {
  id: number;
  text: string;
  isChecked?: boolean;
}

export type EmployeeDetail = ApiSingleData<CliEmpAttributes>;

export type Contract = Data<EmpContractAttributes>;

export interface EmpFile {
  id: string;
  zone: string;
  groupZone: string;
  filename: string;
  size: number;
  updateDate: string;
  uploadDate: string;
  initiatorName: string;
  downloading?: boolean;
  readOnly?: boolean;
}

export interface EmpFilesFolder {
  groupZone: string;
  files: Array<EmpFile>;
  expanded?: boolean;
  updateDate?: string;
}

export enum EmployeeStatus {
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
  FUTURE = 'FUTURE'
}

export const mapContract = (contract: EmpContractAttributes) => {
  contract.haveFlag = false;
  contract.flagUri = '';

  if (contract.links !== undefined && contract.links.length > 0) {
    const flag = contract.links.filter(link => link.rel === 'cliRegFlag');
    if (flag.length > 0) {
      contract.haveFlag = true;
      contract.flagUri = flag[0].href;
    }
  }

  return contract;
};

/**
 * Employee class. Wraps employee data and possible included data (contracts, etc.).
 */
export class Employee {
  public _contracts: EmpContractAttributes[];

  get id(): number {
    return this.data.id;
  }

  set id(value) {
    this.data.id = value;
  }

  get email(): string {
    return this.data.attributes.email;
  }

  set email(value: string) {
    this.data.attributes.email = value;
  }

  get firstName(): string {
    return this.data.attributes.firstName;
  }

  set firstName(value: string) {
    this.data.attributes.firstName = value;
  }

  get lastName(): string {
    return this.data.attributes.lastName;
  }

  set lastName(value: string) {
    this.data.attributes.lastName = value;
  }

  get intFirstName(): string {
    return this.data.attributes.intFirstName;
  }

  set intFirstName(value: string) {
    this.data.attributes.intFirstName = value;
  }

  get intLastName(): string {
    return this.data.attributes.intLastName;
  }

  set intLastName(value: string) {
    this.data.attributes.intLastName = value;
  }

  get otherid1(): string {
    return this.data.attributes.otherid1;
  }

  set otherid1(value: string) {
    this.data.attributes.otherid1 = value;
  }

  get otherid2(): string {
    return this.data.attributes.otherid2;
  }

  get otherid3(): string {
    return this.data.attributes.otherid3;
  }

  get employeeNumber(): string {
    return this.data.attributes.employeeNumber;
  }

  set employeeNumber(value: string) {
    this.data.attributes.employeeNumber = value;
  }

  get specimen(): boolean {
    return this.data.attributes.specimen;
  }

  get actif(): boolean {
    return this.data.attributes.actif;
  }

  get maidenName(): string {
    return this.data.attributes.maidenName;
  }

  get nationalId(): string {
    return this.data.attributes.nationalId;
  }

  set nationalId(value: string) {
    this.data.attributes.nationalId = value;
  }

  get birthDate(): string {
    return this.data.attributes.birthDate;
  }

  get lastUpdateDate(): string {
    return this.data.attributes.lastUpdateDate;
  }

  get cliCoreID(): number {
    return this.data.attributes.cliCoreID;
  }

  get fullName(): string {
    return `${this.firstName || ''} ${this.lastName || ''}`;
  }

  get intFullName(): string {
    return `${this.intFirstName || ''} ${this.intLastName || ''}`;
  }

  get updatePhotoLink(): Link {
    return this.data.links.find(link => link.rel === 'updatePhoto');
  }

  get photoLink(): Link {
    return this.data.links.find(link => link.rel === 'photo');
  }

  set photoLink(value: Link) {
    this.data.attributes.link = value;
  }

  get userActive(): boolean {
    return this.data.attributes.userActive;
  }

  set userActive(value: boolean) {
    this.data.attributes.userActive = value;
  }

  set status(value: EmployeeStatus) {
    this.data.attributes.status = value;
  }

  get status() {
    return this.data.attributes.status;
  }

  get isInactive(): boolean {
    return this.status === EmployeeStatus.INACTIVE;
  }

  get attributes(): CliEmpAttributes {
    return this.data.attributes;
  }

  constructor(public data: Data<CliEmpAttributes>,
              public included?: Array<Data<CliEmpAttributes>>) {
    this.setContracts();
  }

  public getContracts(): Array<Contract> {
    if (!isPresent(this.included)) {
      console.warn('Cannot find contracts for employee: no additional data included with the cliEmp');
      return [];
    }
    return this.included
      .filter(included => included.type === 'empContract')
      .map(mapContract);
  }

  public transformContracts(contracts: any[]) {
    const countContracts: any = {};
    let cliRegNickName: string;
    let indexNumber = 1;
    contracts = contracts.sort((contract1, contract2) => {
      if (!contract1.attributes || !contract2.attributes) {
        return 0;
      }
      if (moment(contract1.attributes.begindate).isAfter(moment(contract2.attributes.begindate))) {
        return -1;
      } else {
        return 1;
      }
    });

    for (const contract of contracts) {
      cliRegNickName = contract.cliRegNickName;
      if (countContracts[cliRegNickName] === undefined) {
        countContracts[cliRegNickName] = 1;
      } else {
        countContracts[cliRegNickName] = countContracts[cliRegNickName] + 1;
      }
    }

    for (const title in countContracts) {
      if (countContracts[title] > 1) {
        contracts.forEach(contract => {
          cliRegNickName = contract.cliRegNickName;
          if (cliRegNickName === title) {
            contract.indexNumber = indexNumber;
            indexNumber++;
          }
        });
      }
    }

    return contracts;
  }

  get contracts(): EmpContractAttributes[] {
    return this._contracts;
  }

  public setContracts() {
    if (!isPresent(this.attributes.empContracts)) {
      console.warn('Cannot find contracts for employee: no additional data included with the cliEmp');
      return [];
    }

    const now = moment();
    this._contracts = this.attributes.empContracts
      .map((contract: EmpContractAttributes) => {

        if (contract.links && contract.links.length > 0) {
          contract.flag = contract.links.find(link => link.rel === 'cliRegFlag');
        }

        return contract;
      }).sort((c1, c2) => {
        const begindate1 = moment(c1.begindate);
        const begindate2 = moment(c2.begindate);
        const enddate1 = moment(c1.enddate);
        const enddate2 = moment(c2.enddate);
        const active1 = now.isBetween(begindate1, enddate1, null, '[]');
        const active2 = now.isBetween(begindate2, enddate2, null, '[]');
        const inFuture1 = begindate1.isAfter(now);
        const inFuture2 = begindate2.isAfter(now);
        const inPast1 = enddate1.isBefore(now);
        const inPast2 = enddate2.isBefore(now);

        if (active1 === active2 && !active1) {
          // both inactive
          if (inFuture1 && inPast2) {
            // take the one in the future
            return -1;
          } else {
            if (inPast1 && inFuture2) {
              // take the one in the future
              return 1;
            } else if (inPast1 && inPast2) {
              // both in past
              if (enddate1.isSame(enddate2)) {
                // same contract end
                if (begindate1.isSame(begindate2)) {
                  // same contract begin
                  // take the last created one (the one with the biggest id)
                  return -(c1.id - c2.id);
                } else {
                  // take the one with the biggest begin date
                  return -begindate1.diff(begindate2);
                }
              } else {
                // take the one with the biggest end date
                return -enddate1.diff(enddate2);
              }
            } else {
              // both in future
              if (begindate1.isSame(begindate2)) {
                // same contract begin
                if (enddate1.isSame(enddate2)) {
                  // same contract end
                  // take the last created one (the one with the biggest id)
                  return -(c1.id - c2.id);
                } else {
                  // take the one with the biggest end date
                  return -enddate1.diff(enddate2);
                }
              } else {
                // take the one with the lowest begin date
                return begindate1.diff(begindate2);
              }
            }
          }
        } else if (active1 === active2) {
          // both active
          if (enddate1.isSame(enddate2)) {
            // same contract end
            if (begindate1.isSame(begindate2)) {
              // same contract begin
              // take the last created one (the one with the biggest id)
              return -(c1.id - c2.id);
            } else {
              // take the one with the oldest begin date
              return begindate1.diff(begindate2);
            }
          } else {
            // take the one with the most recent end date
            return -enddate1.diff(enddate2);
          }
        } else if (active1) {
          // c1 is active
          // c2 is inactive
          return -1;
        }
        // c1 is inactive
        // c2 is active
        return 1;
      });
  }

  public setPhotoUrl(url: string) {
    const photoLinkIndex = this.data.links.findIndex(link => link.rel === 'photo');
    if (photoLinkIndex >= 0) {
      this.data.links[photoLinkIndex].href = url;
    } else {
      this.data.links.push({rel: 'photo', href: url});
    }
  }
}

